import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessagesSquare, Plus, SendHorizonal, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const STARTERS = [
  "What stocks look undervalued right now?",
  "Show companies similar to Nvidia",
  "What would Warren Buffett likely think about Coca-Cola?",
  "How is the market doing today?",
];

const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const MessageBody = ({ content }) => (
  <div className="space-y-1.5">
    {content.split("\n").map((line, i) => (
      <p
        key={i}
        className={`text-sm leading-relaxed ${line.trim().startsWith("- ") ? "pl-3" : ""}`}
        dangerouslySetInnerHTML={{
          __html: esc(line).replace(/\*\*(.+?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>') || "&nbsp;",
        }}
      />
    ))}
  </div>
);

const TickerChips = ({ tickers }) => {
  if (!tickers?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-white/10">
      <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/70 mr-1 self-center">
        Grounded in
      </span>
      {tickers.map((t) => (
        <Link key={t} to={`/stock/${t}`} data-testid={`grounding-chip-${t}`}
          className="px-2 py-0.5 border border-primary/40 bg-primary/10 rounded-sm text-[11px] font-mono text-primary hover:bg-primary/20 transition-colors duration-150">
          {t}
        </Link>
      ))}
    </div>
  );
};

export default function Assistant() {
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [stream, setStream] = useState(null); // {text, grounding}
  const sendingRef = useRef(false);
  const bottomRef = useRef(null);

  const loadConvs = useCallback(async () => {
    const { data } = await api.get("/chat/conversations");
    setConvs(data.conversations);
    return data.conversations;
  }, []);

  useEffect(() => {
    loadConvs().then((list) => {
      if (list.length) setActiveId(list[0].id);
    });
  }, [loadConvs]);

  useEffect(() => {
    if (sendingRef.current) return;
    if (!activeId) {
      setMessages([]);
      return;
    }
    api.get(`/chat/conversations/${activeId}/messages`).then((r) => setMessages(r.data.messages));
  }, [activeId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending, stream]);

  const newConversation = async () => {
    const { data } = await api.post("/chat/conversations", {});
    await loadConvs();
    setActiveId(data.id);
  };

  const deleteConversation = async (id) => {
    await api.delete(`/chat/conversations/${id}`);
    const list = await loadConvs();
    if (activeId === id) setActiveId(list[0]?.id || null);
  };

  const streamSend = async (cid, content) => {
    const token = localStorage.getItem("si_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/conversations/${cid}/messages/stream`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ content }),
    });
    if (!res.ok || !res.body) {
      const err = new Error("stream unavailable");
      err.fallback = true;
      throw err;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let finalMsg = null;
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop();
      for (const part of parts) {
        const line = part.split("\n").find((l) => l.startsWith("data: "));
        if (!line) continue;
        const ev = JSON.parse(line.slice(6));
        if (ev.type === "grounding") setStream((s) => ({ ...(s || { text: "" }), grounding: ev.tickers }));
        else if (ev.type === "delta") setStream((s) => ({ ...(s || { grounding: [] }), text: ((s && s.text) || "") + ev.text }));
        else if (ev.type === "done") finalMsg = ev.message;
        else if (ev.type === "error") throw new Error(ev.message);
      }
      if (finalMsg) break;
    }
    if (!finalMsg) throw new Error("stream ended unexpectedly");
    return finalMsg;
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    let cid = activeId;
    setSending(true);
    sendingRef.current = true;
    setInput("");
    setMessages((m) => [...m, { id: "tmp", role: "user", content, grounding: [] }]);
    setStream({ text: "", grounding: [] });
    try {
      if (!cid) {
        const { data } = await api.post("/chat/conversations", {});
        cid = data.id;
        setActiveId(cid);
      }
      try {
        await streamSend(cid, content);
        const { data } = await api.get(`/chat/conversations/${cid}/messages`);
        setMessages(data.messages);
      } catch (e) {
        if (e.fallback) {
          // SSE unavailable — non-streaming fallback
          const { data } = await api.post(`/chat/conversations/${cid}/messages`, { content });
          setMessages((m) => [...m.filter((x) => x.id !== "tmp"), data.user_message, data.assistant_message]);
        } else {
          toast.error(e.message || "The assistant hit an error. Please try again.");
          const { data } = await api.get(`/chat/conversations/${cid}/messages`);
          setMessages(data.messages);
        }
      }
      loadConvs();
    } catch (e) {
      toast.error(apiErrorDetail(e));
      setMessages((m) => m.filter((x) => x.id !== "tmp"));
      setInput(content);
    } finally {
      setStream(null);
      setSending(false);
      sendingRef.current = false;
    }
  };

  return (
    <div className="fade-up h-[calc(100vh-11.5rem)] flex flex-col">
      <div className="mb-4">
        <div className="overline-label mb-2">AI Research Assistant</div>
        <h1 className="font-heading font-black tracking-tighter text-4xl text-white">Research Assistant</h1>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 min-h-0">
        {/* Conversations */}
        <div className="widget flex flex-col min-h-0" data-testid="conversations-panel">
          <div className="p-3 border-b border-border">
            <Button onClick={newConversation} data-testid="new-conversation-button"
              className="w-full rounded-sm bg-primary hover:bg-primary/85 text-white gap-2 h-9">
              <Plus className="w-4 h-4" /> New conversation
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {convs.length === 0 && (
              <p className="p-4 text-xs text-muted-foreground">No conversations yet.</p>
            )}
            {convs.map((c) => (
              <div key={c.id}
                className={`group flex items-center border-l-2 transition-colors duration-150 ${
                  activeId === c.id ? "border-primary bg-white/5" : "border-transparent hover:bg-white/[0.03]"
                }`}>
                <button onClick={() => setActiveId(c.id)} data-testid={`conversation-item-${c.id}`}
                  className="flex-1 text-left px-3 py-2.5 text-sm text-gray-300 truncate">
                  {c.title}
                </button>
                <button onClick={() => deleteConversation(c.id)} data-testid={`delete-conversation-${c.id}`}
                  className="p-2 text-muted-foreground/40 hover:text-loss opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Chat pane */}
        <div className="widget flex flex-col min-h-0" data-testid="chat-pane">
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {messages.length === 0 && !sending && (
              <div className="h-full flex flex-col items-center justify-center gap-6">
                <MessagesSquare className="w-8 h-8 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">Ask about any company in the 73-stock universe.</p>
                <div className="grid sm:grid-cols-2 gap-2 w-full max-w-xl">
                  {STARTERS.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="starter-prompt"
                      className="text-left text-sm text-gray-300 border border-border rounded-sm px-4 py-3 hover:border-primary/50 hover:bg-white/[0.03] transition-colors duration-150">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={m.id || i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-message-${m.role}`}
                  className={`max-w-[85%] rounded-sm px-4 py-3 ${
                    m.role === "user"
                      ? "bg-primary/15 border border-primary/30 text-gray-100"
                      : "bg-secondary border border-border text-gray-200"
                  }`}
                >
                  <MessageBody content={m.content} />
                  {m.role === "assistant" && <TickerChips tickers={m.grounding} />}
                </div>
              </div>
            ))}
            {stream && stream.text && (
              <div className="flex justify-start" data-testid="chat-message-streaming">
                <div className="max-w-[85%] rounded-sm px-4 py-3 bg-secondary border border-border text-gray-200">
                  <MessageBody content={stream.text} />
                  <span className="inline-block w-2 h-4 bg-primary/70 align-text-bottom live-dot" />
                </div>
              </div>
            )}
            {sending && !(stream && stream.text) && (
              <div className="flex justify-start" data-testid="assistant-thinking">
                <div className="bg-secondary border border-border rounded-sm px-4 py-3 flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  Screening cached data &amp; analysing…
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="p-4 border-t border-border">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder='e.g. "Why does AAPL look risky?"'
                className="rounded-sm bg-background h-10 text-sm"
                data-testid="chat-input"
                disabled={sending}
              />
              <Button onClick={() => send()} disabled={sending || !input.trim()} data-testid="chat-send-button"
                className="rounded-sm bg-primary hover:bg-primary/85 text-white h-10 px-4">
                <SendHorizonal className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground/70 mt-2">
              Analysis grounded in cached market data. This is AI-generated research, not financial advice.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
